import AuthForm from "./components/AuthForm";
import RegisterForm from "./components/Register";
import Dashboard from "./Dashboard";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./components/AuthContext";
import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Spinner } from "@/components/ui/spinner";
import CitationsPage from "./pages/CitationsPage";
import PlansPricing from "./pages/PlansPricing";
import Settings from "./pages/Settings";
import PromptsPage from "./pages/PromptsPage";
import DashboardPage from "./pages/DashboardPage";
import * as Sentry from "@sentry/react";
import CompetitorsPage from "./pages/CompetitorsPage";
import SupportPage from "./pages/SupportPage";
import useCrispVisibility from "./hooks/show-chat"
import { PromptsActionPage } from "./pages/PromptsAction";
import { Foundation } from "./pages/FoundationPage";
import { useVisibilitySocketBridge } from "@/hooks/useVisibilitySocketBridge";
import ForgotPasswordPage from "./pages/ForgotPassword";
import ComingSoonPage from "./pages/ComingSoon";
import { requireEmailVerification } from "@/config/auth";
import { billingAPI } from "./services/api";
import OnboardingPlanPage from "./pages/OnboardingPlanPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import { isBrandMemberTier } from "@/lib/memberTier";
import StrategyPage from "./pages/StrategyPage";
import VerifyEmailLinkPage from "./pages/VerifyEmailLinkPage";
import DataChatPage from "./pages/DataChatPage";
import TryPage from "./pages/TryPage";
import { TRY_PATH } from "@/lib/tryLink";

const EMAIL_VERIFY_PENDING_PATH = "/register/1?verify=pending";
const SHELL_STATUS_STALE_MS = 60_000;

const normalizeTagValue = (value: unknown, fallback = "none"): string => {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
};

const resolveCompanyId = (user: unknown): string => {
  const candidate = user as
    | {
        company?: { id?: string; companyId?: string };
        companyId?: string;
      }
    | null
    | undefined;
  return normalizeTagValue(
    candidate?.company?.id || candidate?.company?.companyId || candidate?.companyId,
  );
};

const resolveCountry = (user: unknown): string => {
  const candidate = user as
    | {
        country?: string;
        company?: { country?: string };
      }
    | null
    | undefined;
  return normalizeTagValue(candidate?.country || candidate?.company?.country, "unknown");
};

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user, emailVerified } = useAuth();
  const queryClient = useQueryClient();
  const [checkingBilling, setCheckingBilling] = useState(false);
  const [billingResolved, setBillingResolved] = useState(false);
  const [canEnterApp, setCanEnterApp] = useState(false);

  useEffect(() => {
    let active = true;
    if (!user?.id || user.onboard_step < 3 || !user.requires_plan_selection) {
      setCanEnterApp(true);
      setCheckingBilling(false);
      setBillingResolved(true);
      return () => {
        active = false;
      };
    }
    setBillingResolved(false);
    setCheckingBilling(true);
    void queryClient
      .fetchQuery({
        queryKey: ["billing-subscription", user.id],
        queryFn: billingAPI.getSubscription,
        staleTime: SHELL_STATUS_STALE_MS,
      })
      .then((subscription) => {
        if (!active) return;
        setCanEnterApp(Boolean(subscription?.can_enter_app));
      })
      .catch(() => {
        if (!active) return;
        setCanEnterApp(false);
      })
      .finally(() => {
        if (!active) return;
        setCheckingBilling(false);
        setBillingResolved(true);
      });
    return () => {
      active = false;
    };
  }, [queryClient, user?.id, user?.onboard_step, user?.requires_plan_selection]);

  if (!user) return <Navigate to="/login" replace />;
  const verified = requireEmailVerification ? emailVerified : true;
  if (verified === false) return <Navigate to={EMAIL_VERIFY_PENDING_PATH} replace />;
  if (user?.isWhitelisted === false && user?.onboard_step < 3) {
    return <Navigate to="/coming-soon" replace />;
  }
  if (user.onboard_step >= 3 && user.requires_plan_selection) {
    if (checkingBilling || !billingResolved) {
      return (
        <div className="w-full h-screen flex justify-center items-center">
          <Spinner variant="circle-filled" size={60}></Spinner>
        </div>
      );
    }
    if (!canEnterApp) {
      return <Navigate to="/onboarding/plan" replace />;
    }
  }
  return children;
};

const RedirectPreserveSearch = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search || ""}`} replace />;
};

const RedirectAfterOnboarding = () => {
  if (typeof window !== "undefined") {
    const shouldRedirectToStrategy =
      window.sessionStorage.getItem("nobori_redirect_to_strategy_once") === "1";
    if (shouldRedirectToStrategy) {
      window.sessionStorage.setItem(
        "nobori_onboarding_strategy_entry_once",
        "1",
      );
      window.sessionStorage.removeItem("nobori_redirect_to_strategy_once");
      return <Navigate to="/dashboard/get-cited/strategy" replace />;
    }
  }
  return <Navigate to="/dashboard/overview" replace />;
};

function App() {
  const { user, emailVerified, loading } = useAuth();
  const location = useLocation();
  const verifyParam = new URLSearchParams(location.search).get("verify");
  const allowVerifyStateOnRegisterStep1 =
    location.pathname === "/register/1" &&
    (verifyParam === "pending" || verifyParam === "invalid");
  const shouldReturnToTry =
    new URLSearchParams(location.search).get("returnTo") === TRY_PATH;
  const routeBrandId = new URLSearchParams(location.search).get("brandId");
  const activeBrand = user?.brands?.find(
    (brand: any) =>
      (brand?.brandId || brand?.id) === routeBrandId,
  );
  const activeAccessRole = activeBrand?.accessRole;
  const isBrandMember = activeAccessRole
    ? activeAccessRole === "brand_member"
    : isBrandMemberTier(user?.tier);
  const onboardStep = Number(user?.onboard_step || 0);
  const shouldGateToPlan = Boolean(
    user && onboardStep >= 3 && user?.requires_plan_selection,
  );
  const verified = requireEmailVerification ? emailVerified : true;
  useCrispVisibility();
  useVisibilitySocketBridge(!!user);

  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      Sentry.setTag("company_id", "none");
      Sentry.setTag("brand_id", "none");
      Sentry.setTag("plan", "none");
      Sentry.setTag("country", "unknown");
      Sentry.setContext("workspace", null);
      return;
    }

    const search = new URLSearchParams(location.search);
    const routeBrandId = normalizeTagValue(
      search.get("brandId") || search.get("brand"),
      "none",
    );
    const companyId = resolveCompanyId(user);
    const plan = normalizeTagValue(user?.tier, "trial");
    const country = resolveCountry(user);
    Sentry.setUser({
      id: String(user.id),
      email: user.email ? String(user.email) : undefined,
    });
    Sentry.setTag("company_id", companyId);
    Sentry.setTag("brand_id", routeBrandId);
    Sentry.setTag("plan", plan);
    Sentry.setTag("country", country);
    Sentry.setContext("workspace", {
      company_id: companyId,
      brand_id: routeBrandId !== "none" ? routeBrandId : undefined,
      plan,
      country,
    });
  }, [location.search, user]);

  if (loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <Spinner variant="circle-filled" size={60}></Spinner>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Navigate
            to={
              shouldGateToPlan
                ? "/onboarding/plan"
                : user && user?.onboard_step >= 3
                  ? "/dashboard"
                  : "/login"
            }
          />
        }
      />
      <Route path="/try" element={<TryPage />} />
      <Route
        path="/login"
        element={
          user && verified === false ? (
            <Navigate to={EMAIL_VERIFY_PENDING_PATH} replace />
          ) : user && user?.isWhitelisted === false && user?.onboard_step < 3 ? (
            <Navigate to="/coming-soon" replace />
          ) : user && user?.onboard_step >= 3 && shouldReturnToTry ? (
            <Navigate to={TRY_PATH} replace />
          ) : shouldGateToPlan ? (
            <Navigate to="/onboarding/plan" replace />
          ) : user && user?.onboard_step >= 3 ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <AuthForm />
          )
        }
      />
      <Route path="/verify/:token" element={<VerifyEmailLinkPage />} />
      <Route
        path="/verify-email"
        element={<Navigate to={EMAIL_VERIFY_PENDING_PATH} replace />}
      />
      <Route
        path="/coming-soon"
        element={
          user && user?.isWhitelisted === false && user?.onboard_step < 3 ? (
            <ComingSoonPage />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/forgot-password"
        element={
          user && verified ? (
            shouldGateToPlan ? (
              <Navigate to="/onboarding/plan" replace />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          ) : (
            <ForgotPasswordPage />
          )
        }
      />
      <Route
        path={`/register/:stepId`}
        element={
          user && verified === false ? (
            allowVerifyStateOnRegisterStep1 ? (
              <RegisterForm />
            ) : (
              <Navigate to={EMAIL_VERIFY_PENDING_PATH} replace />
            )
          ) : user && user?.isWhitelisted === false && user?.onboard_step < 3 ? (
            <Navigate to="/coming-soon" replace />
          ) : shouldGateToPlan ? (
            <Navigate to="/onboarding/plan" replace />
          ) : user && user?.onboard_step >= 3 ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <RegisterForm />
          )
        }
      />
      <Route
        path="/onboarding/plan"
        element={
          !user ? (
            <Navigate to="/login" replace />
          ) : verified === false ? (
            <Navigate to={EMAIL_VERIFY_PENDING_PATH} replace />
          ) : user?.isWhitelisted === false && user?.onboard_step < 3 ? (
            <Navigate to="/coming-soon" replace />
          ) : user?.onboard_step < 3 ? (
            <Navigate to="/register/2" replace />
          ) : !user?.requires_plan_selection ? (
            <RedirectAfterOnboarding />
          ) : (
            <OnboardingPlanPage />
          )
        }
      />
      <Route
        path="/billing/success"
        element={
          !user ? (
            <Navigate to="/login" replace />
          ) : verified === false ? (
            <Navigate to={EMAIL_VERIFY_PENDING_PATH} replace />
          ) : (
            <BillingSuccessPage />
          )
        }
      />
      <Route
        path="/signup"
        element={<RedirectPreserveSearch to="/register/1" />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      >
        <Route index element={<RedirectPreserveSearch to="overview" />} />
        <Route path="overview" element={<DashboardPage />} />
        <Route path="data-chat" element={<DataChatPage />} />
        <Route path="prompts" element={<PromptsPage />} />
        <Route path="competitors" element={<CompetitorsPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="settings">
          <Route
            index
            element={
              <RedirectPreserveSearch to={isBrandMember ? "brand" : "company"} />
            }
          />
          <Route
            path="company"
            element={
              isBrandMember ? (
                <Navigate to="/dashboard/settings/brand" replace />
              ) : (
                <Settings />
              )
            }
          />
          <Route path="brand" element={<Settings />} />
          <Route path="account" element={<Settings />} />
          <Route path="notifications" element={<Settings />} />
          <Route
            path="billing"
            element={
              isBrandMember ? (
                <Navigate to="/dashboard/settings/brand" replace />
              ) : (
                <Settings />
              )
            }
          />
          <Route
            path="integrations"
            element={<RedirectPreserveSearch to="/dashboard/settings/brand" />}
          />
        </Route>
        <Route
          path="plans-pricing"
          element={
            isBrandMember ? (
              <Navigate to="/dashboard/overview" replace />
            ) : (
              <PlansPricing />
            )
        }
      />
         <Route path="get-cited">
          <Route
            index
            element={<RedirectPreserveSearch to="cluster-actions" />}
          />
          <Route path="foundation" element={<Foundation />} />
          <Route path="cluster-actions" element={<PromptsActionPage />} />
          <Route path="strategy" element={<StrategyPage></StrategyPage>} />
        </Route>
        <Route path="citations">
          <Route
            index
            element={<RedirectPreserveSearch to="cited-pages" />}
          />
          <Route path="cited-pages" element={<CitationsPage />} />
          <Route path="cited-domains" element={<CitationsPage />} />
        </Route>
      </Route>

      {/* Fallback 404 */}
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
}

export default App;
